/**
 * A/B Experiment Configuration
 *
 * This file defines the active experiment and its variants.
 * Adjust percentage allocations to control traffic distribution.
 */

export interface ExperimentVariant {
  value: string;
  description: string;
  percentage: number;
}

export interface ExperimentConfig {
  id: string;
  name: string;
  description: string;
  enabled: boolean;
  version: number; // Increment this to force rebucketing
  variants: ExperimentVariant[];
}

/**
 * All experiments in the codebase
 *
 * Add experiments to this array as they are created.
 * Use the `enabled` field to control whether an experiment runs:
 * - enabled: true  → Experiment is active (users are bucketed, data tracked)
 * - enabled: false → Experiment is disabled (killswitch/not yet live)
 *
 * Use cases for enabled: false:
 * - Emergency killswitch if experiment causes issues
 * - Gradual rollout (deploy code disabled, enable via config later)
 * - Experiment concluded but code not yet removed
 */
const ALL_EXPERIMENTS: ExperimentConfig[] = [
  {
    id: 'search_ranking',
    name: 'Search Ranking Test',
    description: 'Test relevance vs random search result ordering',
    enabled: true,
    version: 2,
    variants: [
      {
        value: 'random',
        description: 'Random Search',
        percentage: 80,
      },
      {
        value: 'relevance',
        description: 'Relevance Search',
        percentage: 20,
      },
    ],
  },
  {
    id: 'onboarding_v2',
    name: 'Gated PM Onboarding',
    description:
      'New gated onboarding flow with education and price validation',
    enabled: true,
    version: 1,
    variants: [
      {
        value: 'control',
        description: 'Current flow',
        percentage: 50,
      },
      {
        value: 'gated',
        description: 'New gated flow',
        percentage: 50,
      },
    ],
  },
  {
    id: 'homepage_header_copy',
    name: 'Homepage Header Copy',
    description:
      'AB test the homepage hero headline: current value-prop wording vs. price-focused wording',
    enabled: true,
    version: 1,
    variants: [
      {
        value: 'control',
        description: 'Vacation rentals you want, at the prices you deserve.',
        percentage: 50,
      },
      {
        value: 'price_focused',
        description: 'The vacation rental you want, for hundreds less.',
        percentage: 50,
      },
    ],
  },
  {
    // FE kill switch for the booking-checkout phone-OTP gate. Backend has
    // its own `PHONE_VERIFICATION_ENFORCED` env var; this experiment lets
    // us roll the FE half independently. All envs now run 100% `enforced`.
    // Flip `enabled: false` as an emergency killswitch.
    id: 'phone_verification_gate',
    name: 'Phone Verification Gate',
    description:
      'Require SMS OTP verification before completing a booking checkout',
    enabled: true,
    version: 2,
    variants: [
      {
        value: 'disabled',
        description: 'No verification gate — section hidden, submit unblocked',
        percentage: 0,
      },
      {
        value: 'enforced',
        description:
          'Verification section shown, submit blocked until phone is verified',
        percentage: 100,
      },
    ],
  },
  {
    // Release gate for the host commissions page. All users now see the
    // account nav entry. Flip `enabled: false` as an emergency killswitch;
    // the route itself remains reachable directly.
    id: 'host_commissions',
    name: 'Host Commissions Page',
    description: 'Show the Commissions tab in the host account nav',
    enabled: true,
    version: 2,
    variants: [
      {
        value: 'hidden',
        description: 'Commissions tab not shown in the account nav',
        percentage: 0,
      },
      {
        value: 'visible',
        description: 'Commissions tab shown in the account nav',
        percentage: 100,
      },
    ],
  },
];

/**
 * Active experiments (automatically filtered to only enabled experiments)
 * This is what the ExperimentsProvider uses
 */
export const ACTIVE_EXPERIMENTS: ExperimentConfig[] = ALL_EXPERIMENTS.filter(
  (exp) => exp.enabled
);
